import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import companiesApi, { CompanyDTO } from '../services/companies.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  PlusIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import ConfirmationModal from '../components/ConfirmationModal.tsx';

const formatCurrency = (value?: number | null) => {
  const v = Number(value || 0);
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPercent = (value?: number | null) => {
  const v = Number(value || 0);
  return `${v.toFixed(2)}%`;
};

const formatCNPJ = (cnpj?: string) => {
  const s = (cnpj || '').replace(/\D/g, '').padStart(14, '0');
  return s.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const Companies: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('active');
  const { data, isLoading, isError } = useQuery<CompanyDTO[]>(['companies'], companiesApi.list);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    companyId: number | null;
    companyName: string;
  }>({
    isOpen: false,
    companyId: null,
    companyName: '',
  });

  const removeMutation = useMutation((id: number) => companiesApi.remove(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
    },
  });

  const openDeleteModal = (company: CompanyDTO) => {
    if (!hasPermission('companies.delete')) return;
    setDeleteModal({
      isOpen: true,
      companyId: company.id || null,
      companyName: company.name || '',
    });
  };

  const confirmDelete = async () => {
    if (!deleteModal.companyId) return;
    try {
      await removeMutation.mutateAsync(deleteModal.companyId);
    } finally {
      setDeleteModal({ isOpen: false, companyId: null, companyName: '' });
    }
  };

  const closeDeleteModal = () => {
    if (removeMutation.isLoading) return;
    setDeleteModal({ isOpen: false, companyId: null, companyName: '' });
  };

  const filteredCompanies = (data || []).filter(c => {
    const matchesSearch =
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cnpj?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalValuation = filteredCompanies.reduce((sum, c) => sum + Number(c.valuation || 0), 0);
  const totalAnnualProfit = filteredCompanies.reduce(
    (sum, c) => sum + Number(c.annualProfit || 0),
    0
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 mr-3 text-primary-600" />
            Empresas
          </h1>
          <p className="text-gray-600 mt-2">Gerencie suas empresas e indicadores</p>
        </div>
        {hasPermission('companies.create') && (
          <button
            onClick={() => navigate('/companies/new')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nova Empresa
          </button>
        )}
      </div>

      {/* Loading/Error */}
      {isLoading && (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
      )}
      {isError && <div className="text-center text-red-500">Erro ao carregar empresas.</div>}

      {/* Summary Cards - sempre visíveis, mesmo quando zerados */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Empresas</p>
              <p className="text-2xl font-bold text-gray-900">{filteredCompanies.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valuation Total</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalValuation)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Lucro Anual Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(totalAnnualProfit)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Busca */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou CNPJ..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          {/* Status */}
          <div className="min-w-[160px]">
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value as any)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="active">Ativa</option>
              <option value="all">Todos</option>
              <option value="inactive">Inativa</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid de Empresas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.map(c => (
          <div
            key={c.id}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{c.name}</h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {formatCNPJ(c.cnpj)}
                  </span>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    c.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {c.status === 'active' ? 'Ativa' : 'Inativa'}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Valuation</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(c.valuation)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Lucro Anual</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(c.annualProfit)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">% Lucro</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatPercent(c.profitPercent)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate(`/companies/${c.id}`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Detalhes
                </button>

                {hasPermission('companies.update') && (
                  <button
                    onClick={() => navigate(`/companies/${c.id}/edit`)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Editar
                  </button>
                )}

                {hasPermission('companies.delete') && (
                  <button
                    onClick={() => openDeleteModal(c)}
                    className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                    title="Excluir"
                    aria-label="Excluir"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Excluir Empresa"
        message={`Tem certeza que deseja excluir a empresa "${deleteModal.companyName}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={removeMutation.isLoading}
      />
    </div>
  );
};

export default Companies;
